/* ============================================================
   Compare two saved corridor runs.

   The engine (src/data/compare-runs.js) already decided everything that
   matters — which differences are material, which are noise, and what
   makes a pair not like-for-like. This file only renders that, and it
   deliberately adds no judgement of its own.

   Three UX decisions worth stating, because each is the kind that looks
   arbitrary later:

   · Caveats sit ABOVE the numbers, not in a footnote. A payload gap
     changes how every figure below it should be read, so it has to be
     read first. A caveat underneath the table is a caveat nobody sees.

   · The empty state is a real answer. "No material change" is useful —
     it means the lane held. It renders as a result, not as a failure to
     find anything.

   · Fewer than two saved runs is a dead end, so the page says how to
     get out of it rather than showing two disabled dropdowns.

   The export was added later, and the reason it was missing is worth
   recording. The home page promises "a board-ready PDF from every
   tool"; this tool had none, and neither did the glossary or the saved
   list. Those two are a reference and an index — a PDF of either is a
   printout, not a document. A comparison is different in kind: it is
   already an argument about whether a lane moved and by how much, which
   is the thing that ends up in front of somebody.

   Which two runs were compared lives in the pickers ABOVE the result,
   outside the printed region, so it goes into the subtitle instead. A
   comparison that does not say what it compared is not evidence of
   anything.
   ============================================================ */

import { readRecords } from '../../shared/records.js';
import { compareRuns, summarise } from '../../data/compare-runs.js';
import { html, raw } from '../../shared/html.js';
import { icon } from '../../shared/icons.js';
import { number, shortDate } from '../../shared/format.js';
import { PrintButton } from '../../components/PrintButton.js';

const SEVERITY_LABEL = { danger: 'Material', warning: 'Notable', info: 'Slight', none: 'No change' };

/** Only corridor runs are comparable — the other kinds have no shared metrics. */
function isRun(record) {
  return record?.kind === 'corridor-run' && record?.result;
}

function labelFor(record, index) {
  const r = record.result;
  const lane = r?.cost?.benchmark?.name || r?.input?.corridorKey || 'Run';
  const mode = r?.input?.mode ? ` · ${r.input.mode}` : '';
  return `${lane}${mode} — ${shortDate(record.date)} (#${index + 1})`;
}

/* Formatting has to respect the unit, because "0.07 USD" and "20 %" and
   "315 USD" are three different kinds of number and rounding them the
   same way makes the small ones vanish. */
function fmt(value, unit) {
  if (!Number.isFinite(value)) return '—';
  if (unit === 'USD') return Math.abs(value) < 1 ? number(value, 3) : number(value, 0);
  if (unit === '%') return `${number(value, 1)}%`;
  return number(value, Math.abs(value) < 10 ? 1 : 0);
}

export function CompareTool() {
  const page = document.createElement('div');
  page.className = 'container-narrow page animate-fade-in';

  page.innerHTML = html`
    <header class="page-head">
      <h1>Compare runs</h1>
      <p>
        Two saved corridor runs, side by side. What moved, by how much, and what makes the
        two not directly comparable.
      </p>
    </header>
    <div id="compare-body"><p class="muted">Reading saved work…</p></div>
  `;

  const body = page.querySelector('#compare-body');

  readRecords('benchmarks')
    .then((records) => {
      /* null means storage is unavailable; [] means genuinely empty.
         Collapsing the two would tell someone with a blocked IndexedDB
         that they have saved nothing, which is a different and wrong
         thing to say. */
      if (records === null) {
        body.innerHTML = html`
          <div class="card">
            <p class="muted">
              ${raw(icon('alert', 14))} Saved work could not be read on this device — storage
              may be disabled or full. Nothing has been lost; the comparison simply cannot
              read it from here.
            </p>
          </div>
        `;
        return;
      }

      const runs = records.filter(isRun).sort((a, b) => (a.date < b.date ? 1 : -1));

      if (runs.length < 2) {
        body.innerHTML = html`
          <div class="card card-tinted empty-state">
            <h2>Not enough saved runs yet</h2>
            <p>
              Comparison needs two. ${runs.length === 1
                ? 'There is one saved run on this device.'
                : 'There are no saved runs on this device.'}
              Complete a corridor run and save it, then come back — every run saved from now
              on becomes comparable with the others.
            </p>
            <p><a class="btn btn-primary" href="/corridor-run">Open The Corridor Run</a></p>
          </div>
        `;
        return;
      }

      body.innerHTML = html`
        <section class="section">
          <div class="compare-pickers">
            <label>
              <span>Earlier run</span>
              <select id="pick-a">
                ${runs.map((r, i) => html`<option value="${i}">${labelFor(r, i)}</option>`)}
              </select>
            </label>
            <label>
              <span>Later run</span>
              <select id="pick-b">
                ${runs.map((r, i) => html`<option value="${i}">${labelFor(r, i)}</option>`)}
              </select>
            </label>
          </div>
        </section>
        <div id="compare-result"></div>
      `;

      const pickA = body.querySelector('#pick-a');
      const pickB = body.querySelector('#pick-b');
      const out = body.querySelector('#compare-result');
      pickA.value = '1';
      pickB.value = '0';

      const paint = () => {
        const ia = Number(pickA.value);
        const ib = Number(pickB.value);

        // Comparing a run with itself is not a question anyone has.
        if (ia === ib) {
          out.innerHTML = html`
            <div class="card">
              <p class="muted">
                ${raw(icon('info', 14))} Those are the same run. Pick two different ones.
              </p>
            </div>
          `;
          return;
        }

        const result = compareRuns(runs[ia].result, runs[ib].result);

        out.innerHTML = html`
          ${result.caveats.length
            ? html`<section class="section">
                <div class="card card-warning compare-caveats">
                  <h2>Read these first</h2>
                  ${result.caveats.map((c) => html`<p>${c.note}</p>`)}
                </div>
              </section>`
            : ''}

          <section class="section">
            <h2 class="section-title">${result.unchanged ? 'Result' : `What moved — ${summarise(result)}`}</h2>
            ${result.unchanged
              ? html`<div class="card">
                  <p>
                    <b>No material change between these runs.</b> Every metric moved by less
                    than the precision the inputs support, so nothing here is a result. The
                    lane held.
                  </p>
                </div>`
              : html`<div class="table-wrap">
                  <table class="compare-table">
                    <thead>
                      <tr>
                        <th>Metric</th>
                        <th class="right">Earlier</th>
                        <th class="right">Later</th>
                        <th class="right">Change</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      ${result.deltas.map(
                        (d) => html`<tr class="sev-${d.severity}">
                          <td>${d.label}</td>
                          <td class="right num">${fmt(d.from, d.unit)}</td>
                          <td class="right num">${fmt(d.to, d.unit)}</td>
                          <td class="right num">
                            ${d.fromZero
                              ? 'new'
                              : d.severity === 'none'
                                ? '—'
                                : `${d.change > 0 ? '+' : ''}${number(d.movePct, 1)}%`}
                          </td>
                          <td>
                            <span class="chip chip-${d.improved === null ? 'none' : d.improved ? 'good' : 'bad'}">
                              ${d.severity === 'none'
                                ? SEVERITY_LABEL.none
                                : `${SEVERITY_LABEL[d.severity]} · ${d.improved ? 'better' : 'worse'}`}
                            </span>
                          </td>
                        </tr>`
                      )}
                    </tbody>
                  </table>
                </div>`}
          </section>
        `;

        /* Appended after the paint, not before: paint() rewrites
           out.innerHTML on every change of either picker, which would
           discard a button added once at mount. */
        out.appendChild(
          PrintButton({
            targetId: 'compare-result',
            title: 'Corridor Run Comparison',
            /* The pickers are outside the printed region, so the two
               runs are named here or nowhere. Earlier first, matching
               the column order in the table below it. */
            subtitle: `${labelFor(runs[ia], ia)}  →  ${labelFor(runs[ib], ib)}`,
            /* An unchanged result is still a result — this file's own
               premise — so it exports too, and the footnote has to make
               "nothing moved" mean something on paper. */
            footnote: result.unchanged
              ? 'No metric moved by more than the precision the inputs support. Both runs were computed on this device from figures supplied by the operator; nothing was transmitted. Decision support, not professional advice.'
              : `${summarise(result)}. Movements are graded against the precision the inputs support, so a metric marked “no change” moved by less than its figures can resolve. Both runs were computed on this device from figures supplied by the operator; nothing was transmitted. Decision support, not professional advice.`
          })
        );
      };

      pickA.addEventListener('change', paint);
      pickB.addEventListener('change', paint);
      paint();
    })
    .catch(() => {
      body.innerHTML = html`
        <div class="card">
          <p class="muted">Saved work could not be read on this device.</p>
        </div>
      `;
    });

  return page;
}
